


import {
  defineComponent,
  computed,
  onMounted,
  reactive,
} from '@vue/composition-api';

import { useStore } from '@/hooks/useStore';
import { Pagination } from '@/models/index';
import { TaskForce } from '@/models/apis/taskForce/taskForceResponse';

import { dtFormat, ensureDateWithoutNull } from '@/lib/dateHelper';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import johaisetsuTaskForceApi from '@/apis/johaisetsu/johaisetsu_task_force';
import { redirectIfNoAbility } from '@/lib/abilityHelper';
import { useRoute } from '@/hooks/useRoute';

interface TaskForceEdit {
  id?: number;
  name: string | null;
  start_date: Date;
  end_date: Date | null;
  isNew?: boolean;
}

interface JohaisetsuTaskForce {
  isReady: boolean;

  taskForces: TaskForce[];
  edittingTaskForceOrig?: TaskForceEdit;
  edittingTaskForce: TaskForceEdit;
  deletingTaskForce: TaskForce | null;

  showEditModal: boolean;
  showEditClearConfirmModal: boolean;
  showDeleteModal: boolean;
  showErrorModal: boolean;
  errorBody: string;
  dateErrorBody: string;
  pager: Pagination;
}

interface DateRange {
  start: Date;
  end: Date | null;
}

export default defineComponent({
  name: 'johaisetsu-task-force',
  setup() {
    const state = reactive<JohaisetsuTaskForce>({
      isReady: false,

      taskForces: [],
      edittingTaskForce: {
        name: null,
        start_date: new Date(),
        end_date: null,
      },
      deletingTaskForce: null,

      showEditModal: false,
      showEditClearConfirmModal: false,
      showDeleteModal: false,
      showErrorModal: false,
      errorBody: '',
      dateErrorBody: '',
      pager: {
        currentPage: 1,
        itemsPerPage: 50,
      },
    });
    const store = useStore();
    const userState = store.state.user;
    const showWaitSpinner = computed<boolean>(() => {
      return !state.isReady;
    });
    const paginatedTaskForces = computed<TaskForce[]>(() => {
      const nextPageIdx = state.pager.currentPage * state.pager.itemsPerPage;
      const minIdx = nextPageIdx - state.pager.itemsPerPage;
      return state.taskForces.filter((e, i) => {
        return i >= minIdx && i < nextPageIdx;
      });
    });
    const shouldShowEditModal = computed<boolean>(() => {
      if (state.showErrorModal) { return false; }
      if (state.showEditClearConfirmModal) { return false; }
      return state.showEditModal;
    });
    const shouldShowDeleteModal = computed<boolean>(() => {
      if (state.showErrorModal) { return false; }
      return state.showDeleteModal;
    });
    const isReadyToSave = computed<boolean>(() => {
      return checkInputs();
    });

    const getTaskForces = async() => {
      state.isReady = false;
      const { data } = await johaisetsuTaskForceApi.index();
      state.taskForces = data.map(taskForce => ({
        ...taskForce,
        start_date: ensureDateWithoutNull(taskForce.start_date),
        end_date: taskForce.end_date ? ensureDateWithoutNull(taskForce.end_date) : null,
      }));
      state.isReady = true;
    };
    const openEditModal = (item?: TaskForce) => {
      const obj: TaskForceEdit = {
        ...(item ? {id: item.id} : null),
        name: item?.name ?? null,
        start_date: item?.start_date ?? new Date(),
        end_date: item?.end_date ?? null,
        isNew: !item || !item.id,
      };
      prepareOrigValues(obj);
      state.edittingTaskForce = obj;
      state.showEditModal = true;
    };
    const tryCloseEditModal = () => {
      if (isEdited()) {
        state.showEditClearConfirmModal = true;
      } else {
        state.showEditModal = false;
      }
    };
    const closeEditModal = () => {
      state.edittingTaskForce = {} as TaskForceEdit;
      state.showEditClearConfirmModal = false;
      state.showEditModal = false;
    };
    const getTaskForceForReq = (taskForce: TaskForceEdit) => {
      const reqObj = JSON.parse(JSON.stringify(taskForce));

      reqObj.start_date = dtFormat(taskForce.start_date, 'yyyy-mm-dd');
      reqObj.end_date = dtFormat(taskForce.end_date, 'yyyy-mm-dd');

      return reqObj;
    };
    const saveTaskForce = async() => {
      try {
        const reqObj = getTaskForceForReq(state.edittingTaskForce);
        if (state.edittingTaskForce.isNew) {
          await johaisetsuTaskForceApi.create(reqObj);
        } else {
          const taskForceId = state.edittingTaskForce.id;
          if (!taskForceId) {
            return;
          }
          await johaisetsuTaskForceApi.update(taskForceId, reqObj);
        }
        await getTaskForces();
        closeEditModal();
      } catch (e) {
        console.error('error', e);
        state.errorBody = '削除に失敗しました。再度操作を行ってください';
        state.showErrorModal = true;
      }
    };
    const openDeleteModal = (item: TaskForce) => {
      state.deletingTaskForce = item;
      state.showDeleteModal = true;
    };
    const closeDeleteModal = () => {
      state.deletingTaskForce = {} as TaskForce;
      state.showDeleteModal = false;
    };
    const deleteTaskForce = async() => {
      try {
        if (state.deletingTaskForce == null) return;
        await johaisetsuTaskForceApi.destroy(state.deletingTaskForce.id);
        await getTaskForces();
        closeDeleteModal();
      } catch (e) {
        console.error('error', e);
        state.errorBody = '削除に失敗しました。再度操作を行ってください';
        state.showErrorModal = true;
      }
    };
    const prepareOrigValues = (obj: TaskForceEdit) => {
      state.edittingTaskForceOrig = obj;
    };
    const checkInputs = (): boolean => {
      const obj = state.edittingTaskForce;
      if (hasDateRangeCheck(obj.start_date, obj.end_date)) {
        return false;
      }
      if (
        !obj.name ||
        !obj.start_date
      ) {
        return false;
      }
      if (obj.name.match(/^\s*$/)) {
        return false;
      }

      return true;
    };
    const isEdited = () => {
      const obj = state.edittingTaskForce;
      const objOrig = state.edittingTaskForce;

      if (obj.name !== objOrig.name) return true;
      if (obj.start_date !== objOrig.start_date) return true;
      if (obj.end_date !== objOrig.end_date) return true;
      return false;
    };
    const isDateBetweenDateRange = (range: DateRange, date: Date): boolean => {
      if (!range.end) {
        return range.start <= date;
      }
      return range.start <= date && date <= range.end;
    };

    const hasDateRangeIntersection = (range1: DateRange, range2: DateRange): boolean => {
      if (!range1.end || !range2.end) {
        return isDateBetweenDateRange(range1, range2.start) || isDateBetweenDateRange(range2, range1.start);
      }
      return range1.start <= range2.end && range2.start <= range1.end;
    };
    const hasDateRangeCheck = (start: Date | null, end: Date | null): boolean => {
      if (!start) return false;

      if (end && end < start) {
        state.dateErrorBody = '終了日は開始日以降の日付を入力してください。';
        return true;
      }
      const taskForceId = state.edittingTaskForce.id;
      for (const taskForce of state.taskForces) {
        if (
          taskForceId !== taskForce.id &&
          hasDateRangeIntersection({start: taskForce.start_date, end: taskForce.end_date}, {start, end})
        ) {
          state.dateErrorBody = '日付範囲が重複している対策本部がすでに登録されています。';
          return true;
        }
      }
      return false;
    };
    const { route } = useRoute();
    onMounted(async() => {
      await waitForUserAndMasters();
      redirectIfNoAbility(userState, route.value);
      await getTaskForces();

      state.isReady = true;
    });
    return {
      state,
      // computed
      showWaitSpinner,
      paginatedTaskForces,
      shouldShowEditModal,
      shouldShowDeleteModal,
      isReadyToSave,
      // methods
      openEditModal,
      tryCloseEditModal,
      closeEditModal,
      hasDateRangeCheck,
      saveTaskForce,
      openDeleteModal,
      closeDeleteModal,
      deleteTaskForce,
      dtFormat,
    };
  },
});
